import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Wand2, Printer, Loader2, FileText, Layers, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import LikeButton from '@/components/LikeButton';

const KIND_CONFIG = {
  worksheet: { label: 'Worksheet', icon: FileText, color: 'text-emerald-600' },
  flashcards: { label: 'Flashcards', icon: Layers, color: 'text-blue-600' },
  story_prompt: { label: 'Story Prompt', icon: PenLine, color: 'text-purple-600' },
};

const SUBJECTS = ['reading', 'math', 'science', 'writing'];
const BANDS = ['PreK-K', 'K-1', '2-3', '4-5'];

const MaterialsGeneratorPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialChild = searchParams.get('child') || '';
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(initialChild);
  const [form, setForm] = useState({ kind: 'worksheet', subject: 'math', topic: '', grade_band: 'K-1', count: 5 });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  // Fetch parent's children for the child-picker (needed to save likes to a library)
  useEffect(() => {
    api.get('/children').then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.children || []);
      setChildren(list);
      // Default to the first child if none in URL
      if (!initialChild && list.length > 0) {
        setSelectedChild(list[0].id);
      }
    }).catch(() => {});
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep URL in sync when child changes so likes persist across navigation
  useEffect(() => {
    if (selectedChild) {
      const next = new URLSearchParams(searchParams);
      next.set('child', selectedChild);
      setSearchParams(next, { replace: true });
    }
  }, [selectedChild]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/materials/history').then((r) => setHistory(r.data.materials || [])).catch(() => {});
  }, [api]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.topic.trim()) {
      toast.error('Please enter a topic.');
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const payload = { ...form, child_id: selectedChild || undefined };
      const res = await api.post('/materials/generate', payload);
      setResult(res.data);
      toast.success('Material ready!');
      // refresh history
      api.get('/materials/history').then((r) => setHistory(r.data.materials || [])).catch(() => {});
    } catch (err) {
      toast.error('Generation failed. Try a different topic or retry.');
    } finally {
      setGenerating(false);
    }
  };

  const printResult = () => {
    if (!result) return;
    const w = window.open('', 'materialPrint');
    if (!w) {
      toast.error('Popup blocked — allow popups to print.');
      return;
    }
    w.document.write(`<!DOCTYPE html><html><head><title>${result.material?.title || 'Material'}</title>
<style>body{font-family:system-ui,sans-serif;padding:32px;color:#1e293b;} h1{margin-top:0;color:#065f46;}
.card{border:1px solid #e2e8f0;border-radius:8px;padding:12px;margin:8px 0;}
.answer{color:#94a3b8;font-size:12px;margin-top:6px;} @media print{.no-print{display:none;}}</style></head><body>`);
    w.document.write(`<h1>${result.material?.title || 'Untitled'}</h1>`);
    w.document.write(`<p><em>${result.subject} · ${result.grade_band} · ${result.kind}</em></p>`);
    if (result.material?.instructions) w.document.write(`<p><strong>${result.material.instructions}</strong></p>`);
    if (result.material?.prompt) w.document.write(`<p>${result.material.prompt}</p>`);
    (result.material?.items || []).forEach((it, i) => {
      w.document.write(`<div class="card"><strong>${i + 1}.</strong> ${it.prompt || ''}<div class="answer">Answer key: ${it.answer || ''}</div></div>`);
    });
    (result.material?.cards || []).forEach((c, i) => {
      w.document.write(`<div class="card"><strong>${i + 1}. Front:</strong> ${c.front || ''}<div class="answer"><strong>Back:</strong> ${c.back || ''}</div></div>`);
    });
    (result.material?.story_starters || []).forEach((s, i) => {
      w.document.write(`<div class="card"><strong>${i + 1}.</strong> ${s}</div>`);
    });
    if (result.material?.checklist?.length) {
      w.document.write('<h3>Checklist</h3><ul>');
      result.material.checklist.forEach((c) => w.document.write(`<li>${c}</li>`));
      w.document.write('</ul>');
    }
    w.document.write(`<div class="no-print" style="margin-top:24px;"><button onclick="window.print()">Print</button></div></body></html>`);
    w.document.close();
  };

  const renderResult = () => {
    if (!result?.material) return null;
    const m = result.material;
    return (
      <Card className="border-2 border-emerald-300 bg-emerald-50/40" data-testid="materials-result">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">{result.kind}</p>
              <h2 className="text-xl font-extrabold text-slate-900">{m.title || 'Material'}</h2>
              <div className="flex gap-1 mt-1 flex-wrap">
                <Badge variant="outline" className="text-xs">{result.subject}</Badge>
                <Badge variant="outline" className="text-xs">{result.grade_band}</Badge>
              </div>
            </div>
            <Button onClick={printResult} className="bg-emerald-600 hover:bg-emerald-700" data-testid="materials-print">
              <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
            </Button>
          </div>
          {selectedChild && (
            <div className="flex items-center justify-end -mt-2">
              <LikeButton
                childId={selectedChild}
                contentType="generated_material"
                contentId={result.id}
                size="md"
                metadata={{
                  title: m.title || result.topic,
                  subject: result.subject,
                  band: result.grade_band,
                  kind: result.kind,
                }}
                testIdPrefix="materials-like"
              />
            </div>
          )}
          {m.instructions && <p className="text-sm text-slate-700 italic">{m.instructions}</p>}
          {m.prompt && <p className="text-sm text-slate-800">{m.prompt}</p>}
          {m.items?.length > 0 && (
            <ol className="space-y-2">
              {m.items.map((it, i) => (
                <li key={i} className="bg-white rounded-lg p-3 border border-slate-200 text-sm">
                  <p className="font-semibold text-slate-800">{i + 1}. {it.prompt}</p>
                  <p className="text-xs text-slate-500 mt-1">Answer: {it.answer}</p>
                </li>
              ))}
            </ol>
          )}
          {m.cards?.length > 0 && (
            <div className="grid md:grid-cols-2 gap-2">
              {m.cards.map((c, i) => (
                <div key={i} className="bg-white rounded-lg p-3 border border-slate-200 text-sm">
                  <p className="font-bold text-slate-800">{c.front}</p>
                  <p className="text-xs text-slate-500 mt-1">→ {c.back}</p>
                </div>
              ))}
            </div>
          )}
          {m.story_starters?.length > 0 && (
            <>
              <p className="text-sm font-semibold text-slate-700">Story starters:</p>
              <ul className="list-disc list-inside space-y-1">
                {m.story_starters.map((s, i) => (
                  <li key={i} className="text-sm text-slate-700">{s}</li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="materials-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <Link to="/dashboard" className="inline-block hover:opacity-70 transition-opacity" data-testid="materials-title-link">
              <h1 className="text-xl font-extrabold text-slate-900 flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-emerald-500" /> Materials Generator
              </h1>
            </Link>
            <p className="text-xs text-slate-500">Parent & teacher tools — AI-generated worksheets, flashcards, and story prompts on demand.</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {children.length > 0 && (
          <Card className="border border-slate-200 bg-slate-50/50">
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <label className="text-xs font-semibold text-slate-600 whitespace-nowrap">Save to child&apos;s library:</label>
              <select
                className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
                value={selectedChild}
                onChange={(e) => setSelectedChild(e.target.value)}
                data-testid="materials-child-picker"
              >
                <option value="">— None (don&apos;t save) —</option>
                {children.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 flex-1 min-w-[200px]">
                Pick a child so you can heart materials into their personal library.
              </p>
            </CardContent>
          </Card>
        )}
        <Card className="border border-slate-200">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="materials-kind-tabs">
              {Object.entries(KIND_CONFIG).map(([k, cfg]) => {
                const Icon = cfg.icon;
                const active = form.kind === k;
                return (
                  <button
                    key={k}
                    type="button"
                    className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 text-xs font-semibold transition-all ${
                      active ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'
                    }`}
                    onClick={() => setForm((p) => ({ ...p, kind: k }))}
                    data-testid={`materials-kind-${k}`}
                  >
                    <Icon className={`w-5 h-5 ${cfg.color}`} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Subject</label>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.subject}
                  onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                  data-testid="materials-subject"
                >
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Grade band</label>
                <select
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.grade_band}
                  onChange={(e) => setForm((p) => ({ ...p, grade_band: e.target.value }))}
                  data-testid="materials-band"
                >
                  {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-slate-600">Topic</label>
                <Input
                  value={form.topic}
                  onChange={(e) => setForm((p) => ({ ...p, topic: e.target.value }))}
                  placeholder="e.g. Adding within 20, Long vowel patterns, Animals of the rainforest"
                  data-testid="materials-topic"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Count</label>
                <Input
                  type="number"
                  min={3}
                  max={20}
                  value={form.count}
                  onChange={(e) => setForm((p) => ({ ...p, count: e.target.value }))}
                  data-testid="materials-count"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={generating}
                  data-testid="materials-generate"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                  Generate
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {renderResult()}

        {history.length > 0 && (
          <Card className="border border-slate-200">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-slate-800 mb-3">Recent materials</p>
              <div className="space-y-2">
                {history.slice(0, 8).map((h) => (
                  <button
                    key={h.id}
                    className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 transition-all"
                    onClick={() => setResult({ id: h.id, kind: h.kind, subject: h.subject, topic: h.topic, grade_band: h.grade_band, material: h.material })}
                    data-testid={`materials-history-${h.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800 text-sm">{h.material?.title || h.topic}</p>
                      <div className="flex gap-1">
                        <Badge variant="outline" className="text-xs">{h.kind}</Badge>
                        <Badge variant="outline" className="text-xs">{h.subject}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{new Date(h.generated_at).toLocaleString()}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default MaterialsGeneratorPage;
